import { useEffect, useState } from "react";
import { getStats, listOrders, getSettings } from "@/lib/api";
import { TrendUp, Package, Truck, CheckCircle, ChatCircleText, Sparkle, CurrencyDollar, Clock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const Stat = ({ label, value, icon: Icon, tone = "default", testId }) => {
  const tones = {
    default: "text-arena-text",
    primary: "text-arena-primary",
    danger: "text-arena-danger",
    success: "text-arena-success",
    warning: "text-arena-warning",
  };
  return (
    <div className="border border-arena-border bg-arena-surface p-5 hover:border-arena-primary transition-colors duration-150" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon size={18} weight="bold" className={tones[tone]} />
      </div>
      <div className={`font-heading text-3xl font-bold mt-3 ${tones[tone]}`}>{value}</div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    placed: "bg-arena-warning text-black",
    processed: "bg-arena-primary text-black",
    shipped: "bg-blue-400 text-black",
    delivered: "bg-arena-success text-black",
    cancelled: "bg-arena-danger text-white",
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] tracking-widest font-bold uppercase ${styles[status] || 'bg-arena-secondary text-arena-text'}`}>
      {status}
    </span>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    try {
      const [s, o, cfg] = await Promise.all([getStats(), listOrders(), getSettings()]);
      setStats(s);
      setRecentOrders(o.slice(0, 6));
      setSettings(cfg);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 grid-lines min-h-screen" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline mb-2">// mission control</div>
          <h1 className="font-heading text-5xl font-bold tracking-tight">
            HEAVY <span className="text-arena-primary">LIFTING</span>.<br/>DONE.
          </h1>
          <p className="text-arena-muted mt-3 text-sm">Your dropshipping empire — orders, ads, and support in one command center.</p>
        </div>
        <div className="text-right">
          <div className="overline">store</div>
          <div className="font-heading text-lg mt-1" data-testid="dashboard-store-url">{(settings?.store_url || "").replace(/^https?:\/\//, "") || "pepstyles.store"}</div>
          <div className="overline mt-3">powered by</div>
          <div className="font-heading text-arena-primary text-sm">SELLVIA + STRIPE</div>
        </div>
      </div>

      {/* North Star */}
      <div className="border border-arena-border bg-arena-surface p-6 mb-4 relative overflow-hidden" data-testid="northstar-card">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="overline text-arena-primary">// north star</div>
            <div className="font-heading text-7xl font-bold text-arena-primary mt-2">
              {stats?.total_orders ?? "—"}
            </div>
            <div className="text-arena-muted mt-1 text-sm">total orders processed</div>
          </div>
          <div className="text-right">
            <div className="overline">est. revenue</div>
            <div className="font-heading text-4xl font-bold text-arena-success mt-2" data-testid="revenue-value">
              ${stats?.revenue?.toFixed(2) ?? "0.00"}
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat testId="stat-pending" label="pending action" value={stats?.pending_action ?? "—"} icon={Clock} tone="warning" />
        <Stat testId="stat-processed" label="processed" value={stats?.processed ?? "—"} icon={Package} tone="primary" />
        <Stat testId="stat-shipped" label="shipped" value={stats?.shipped ?? "—"} icon={Truck} />
        <Stat testId="stat-delivered" label="delivered" value={stats?.delivered ?? "—"} icon={CheckCircle} tone="success" />
        <Stat testId="stat-ads" label="ads generated" value={stats?.ads_generated ?? "—"} icon={Sparkle} tone="primary" />
        <Stat testId="stat-chat-sessions" label="chat sessions" value={stats?.chat_sessions ?? "—"} icon={ChatCircleText} />
        <Stat testId="stat-messages" label="bot messages" value={stats?.chat_messages ?? "—"} icon={TrendUp} />
        <Stat testId="stat-cancelled" label="cancelled" value={stats?.cancelled ?? "—"} icon={CurrencyDollar} tone="danger" />
      </div>

      {/* Recent orders feed */}
      <div className="border border-arena-border bg-arena-surface" data-testid="recent-orders">
        <div className="flex items-center justify-between px-5 py-4 border-b border-arena-border">
          <div>
            <div className="overline">// recent orders</div>
            <div className="font-heading text-xl font-semibold mt-1">Live Feed</div>
          </div>
          <Link to="/orders" className="text-xs tracking-widest uppercase text-arena-primary hover:underline" data-testid="link-all-orders">
            View all →
          </Link>
        </div>
        <div className="divide-y divide-arena-border">
          {recentOrders.length === 0 && (
            <div className="p-8 text-center text-arena-muted text-sm">
              No orders yet. Head to <Link to="/orders" className="text-arena-primary underline">Orders</Link> to add or import your first batch.
            </div>
          )}
          {recentOrders.map(o => (
            <div key={o.id} className="px-5 py-4 flex items-center gap-4 hover:bg-arena-surfaceHover transition-colors" data-testid={`recent-row-${o.order_number}`}>
              <div className="font-heading text-arena-muted text-sm w-24 truncate">{o.order_number}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{o.customer_name}</div>
                <div className="text-xs text-arena-muted truncate">{o.product_name}</div>
              </div>
              <div className="font-heading text-sm text-arena-primary w-20 text-right">${(o.price * o.quantity).toFixed(2)}</div>
              <StatusBadge status={o.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
